import { VILLES_INSCRIPTION } from "@hakili/domain-types";
import { t } from "@hakili/i18n";
import { Button, Card } from "@hakili/ui";
import { useState } from "react";

import {
  DeclaredProfileFields,
  draftFromView,
  toWrite,
  validateDraft,
  type ProfileView,
  type ProfileWrite,
} from "./declared-profile.js";

const CHAMP = "w-full rounded-xl border border-gray-200 px-3 py-3 text-base";

/**
 * `REG-PROFILE-001`, Arc B: **the profile editor, where a client keeps his declaration current.**
 *
 * Step 0 of `docs/claude/underwriter-workflow.md`: the profile is « updated by the client over time ». Same fields
 * as registration (`DeclaredProfileFields`), same door (`PUT /api/v1/me/profile`). The name and the city are
 * written beside the profile in one save; the phone is shown and never edited here (changing a login number is
 * its own flow, with its own proof).
 */
export function ProfilDeclare({
  view,
  enregistrer,
  retour,
  refusal,
}: {
  readonly view: ProfileView;
  readonly enregistrer: (
    write: ProfileWrite & { readonly fullName: string; readonly city: string },
  ) => Promise<void>;
  readonly retour: () => void;
  readonly refusal?: string | undefined;
}): React.JSX.Element {
  const [draft, setDraft] = useState(() => draftFromView(view));
  const [fullName, setFullName] = useState(view.fullName);
  const [city, setCity] = useState(view.city ?? VILLES_INSCRIPTION[0] ?? "");
  const [attempted, setAttempted] = useState(false);
  const [enCours, setEnCours] = useState(false);

  const soumettre = (): void => {
    if (enCours) return;
    setAttempted(true);
    if (Object.keys(validateDraft(draft)).length > 0 || fullName.trim().length <= 2) return;
    setEnCours(true);
    void enregistrer({ ...toWrite(draft), fullName: fullName.trim(), city }).finally(() => {
      setEnCours(false);
    });
  };

  return (
    <Card shape="client" className="flex flex-col gap-4">
      <Button variant="ghost" data-testid="declaration-retour" onClick={retour}>
        {t("client.declaration.retour")}
      </Button>
      <h1 className="text-xl font-semibold text-gray-900">{t("client.declaration.titre")}</h1>
      <label className="flex flex-col gap-1 text-sm text-gray-500" htmlFor="declaration-nom">
        {t("client.declaration.nom")}
        <input
          id="declaration-nom"
          data-testid="declaration-nom"
          className={CHAMP}
          autoComplete="name"
          value={fullName}
          onChange={(event) => {
            setFullName(event.target.value);
          }}
        />
      </label>
      <label className="flex flex-col gap-1 text-sm text-gray-500" htmlFor="declaration-ville">
        {t("client.declaration.ville")}
        <select
          id="declaration-ville"
          data-testid="declaration-ville"
          className={CHAMP}
          value={city}
          onChange={(event) => {
            setCity(event.target.value);
          }}
        >
          {(VILLES_INSCRIPTION.includes(city) ? VILLES_INSCRIPTION : [city, ...VILLES_INSCRIPTION]).map(
            (une) => (
              <option key={une} value={une}>
                {une}
              </option>
            ),
          )}
        </select>
      </label>
      <p className="flex flex-col gap-1 text-sm text-gray-500">
        {t("client.declaration.telephone")}
        <span data-testid="declaration-telephone" className="text-base text-gray-900">
          {view.phone}
        </span>
      </p>
      <DeclaredProfileFields draft={draft} onChange={setDraft} attempted={attempted} />
      {refusal === undefined ? null : (
        <p role="alert" className="text-sm text-rose-700">
          {refusal}
        </p>
      )}
      <Button data-testid="declaration-enregistrer" onClick={soumettre}>
        {enCours ? t("client.declaration.enregistrement") : t("client.declaration.enregistrer")}
      </Button>
    </Card>
  );
}
